// Customer profile. Every role that holds the Customers view can read it;
// editing needs the write grant. A clinic reads the account information, the
// protocol and the activity; contacts and
// collection sites need their own grant, and the server leaves them out of a
// clinic's response entirely.
//
// The record sits on the left and the relationship on the right; the cards
// themselves live in CustomerProfileCards.jsx.

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getCustomer, agreementUrl, listFranchisees, removeCustomer } from '../api.js'
import { useAuth } from '../auth.jsx'
import { ACTION } from '../../../shared/permissions.js'
import { useConfirm } from '../confirm.jsx'
import { Button, ButtonLink, Detail, Notice, Panel, Pill } from '../components/ui/index.js'
import { customerStatusTone } from '../tones/customers.js'
import {
  statusLabel,
  formatMoney,
  formatCount,
  formatDate,
  formatTenure,
  SEPARATOR,
} from '../vocab.js'
import {
  SplitCard,
  PricingCard,
  ClinicBillRatesCard,
  ContactsCard,
  SitesCard,
} from './CustomerProfileCards.jsx'
import { IssuesCard } from './CustomerIssuesCard.jsx'
import { ProtocolRows } from '../protocol.jsx'

export default function CustomerProfile() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { can } = useAuth()
  const canWrite = can(ACTION.CUSTOMERS_WRITE)
  const canManageSplit = can(ACTION.CUSTOMERS_MANAGE_SPLIT)
  const canManagePricing = can(ACTION.CUSTOMERS_MANAGE_PRICING)
  const canReadIssues = can(ACTION.ISSUES_READ)
  const canWriteIssues = can(ACTION.ISSUES_WRITE)
  const canReadContacts = can(ACTION.CUSTOMERS_CONTACTS_READ)
  const canWriteBillRates = can(ACTION.BILL_RATES_WRITE)
  const confirm = useConfirm()
  const [data, setData] = useState(null)
  const [franchisees, setFranchisees] = useState([])
  const [loadError, setLoadError] = useState(null)
  const [rosterError, setRosterError] = useState(null)
  const [removeError, setRemoveError] = useState(null)

  const load = useCallback(async () => {
    try {
      setData(await getCustomer(id))
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught.message)
    }
  }, [id])

  useEffect(() => {
    load()
    listFranchisees()
      .then((result) => {
        setFranchisees(result.franchisees)
        setRosterError(null)
      })
      .catch((caught) => setRosterError(caught.message))
  }, [load])

  async function drop() {
    if (!(await confirm(`Remove ${data.customer.name}? Their history is kept.`))) return
    try {
      await removeCustomer(id)
      navigate('/customers')
    } catch (caught) {
      setRemoveError(caught.message)
    }
  }

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }
  if (!data) return <div className="center-note">Loading</div>

  const { customer, contacts, sites } = data

  return (
    <>
      <header className="page-head">
        <h1>{customer.name}</h1>
        <p className="support">
          <Pill tone={customerStatusTone[customer.status]}>{statusLabel(customer.status)}</Pill>{' '}
          {customer.customerSince ? `Customer for ${formatTenure(customer.customerSince)}` : null}
        </p>
      </header>

      {removeError ? <Notice tone="bad">{removeError}</Notice> : null}

      <div className="toolbar">
        {customer.hubspotUrl ? (
          <a
            className="ui-btn ui-btn-secondary"
            href={customer.hubspotUrl}
            target="_blank"
            rel="noreferrer"
          >
            Open in HubSpot
          </a>
        ) : null}
        <span className="spacer" />
        {canWrite ? (
          <>
            <Button variant="ghost" onClick={drop}>
              Remove
            </Button>
            <ButtonLink variant="primary" to={`/customers/${id}/edit`}>
              Edit customer
            </ButtonLink>
          </>
        ) : null}
      </div>

      {rosterError ? (
        <Notice tone="bad">The clinic list could not be loaded: {rosterError}</Notice>
      ) : null}

      <div className="split">
        <div className="stack">
          <Panel title="Account">
            <Detail label="Legal entity">{customer.legalEntity}</Detail>
            <Detail label="Agreement">
              {customer.agreement ? (
                canWrite ? (
                  <a href={agreementUrl(customer.id)}>{customer.agreement.fileName}</a>
                ) : (
                  customer.agreement.fileName
                )
              ) : null}
            </Detail>
            <Detail label="Customer since">
              {customer.customerSince
                ? `${formatDate(customer.customerSince)}${SEPARATOR}${formatTenure(customer.customerSince)}`
                : null}
            </Detail>
            <Detail label="Contract ends">{formatDate(customer.contractEnd)}</Detail>
            {customer.notes ? <p className="card-note muted">{customer.notes}</p> : null}
          </Panel>

          {/* The issue log lives in the wide column: descriptions are
              sentences and the narrow aside would fold them word by word. */}
          {canReadIssues ? <IssuesCard customerId={id} canWrite={canWriteIssues} /> : null}

          {canManagePricing ? <PricingCard customerId={id} /> : null}
          <ClinicBillRatesCard customerId={id} canWrite={canWriteBillRates} />
          {canReadContacts ? (
            <>
              <ContactsCard customerId={id} contacts={contacts ?? []} onChanged={load} />
              <SitesCard
                customerId={id}
                sites={sites ?? []}
                franchisees={franchisees}
                onChanged={load}
              />
            </>
          ) : null}
        </div>

        <div className="stack">
          <Panel title="Protocol" support="How collections are handled for this account.">
            <ProtocolRows customer={customer} />
          </Panel>

          <SplitCard customer={customer} canWrite={canManageSplit} onChanged={load} />

          {/* FTFS revenue (customer bills) is nested under rollups.ftfs only for
              a viewer who may read it; everyone sees the franchisee side. */}
          {customer.rollups.ftfs ? (
            <Panel title="Year to Date Activity" support="FTFS Year to Date Revenue">
              <Detail label="Revenue this year">
                <span className="tabular">{formatMoney(customer.rollups.ftfs.ytdRevenue)}</span>
              </Detail>
              <Detail label="Tests this year">
                <span className="tabular">{formatCount(customer.rollups.ftfs.ytdTests)}</span>
              </Detail>
              <p className="card-note muted">
                From customer bills. Lifetime figures are on the directory.
              </p>
            </Panel>
          ) : null}

          <Panel
            title="Franchisee Revenue Year to Date"
            support="What the clinics invoiced FTFS for this customer this year."
          >
            <Detail label="Revenue this year">
              <span className="tabular">{formatMoney(customer.rollups.ytdRevenue)}</span>
            </Detail>
            <Detail label="Tests this year">
              <span className="tabular">{formatCount(customer.rollups.ytdTests)}</span>
            </Detail>
            <p className="card-note muted">
              From franchisee invoices. Month by month detail lives in the Activity tab.
            </p>
          </Panel>
        </div>
      </div>
    </>
  )
}
